import { useTranslation } from 'next-i18next'
// Dependencies.
import styled from 'styled-components'
import MOMENT from 'moment'
import { useContext, useState } from 'react'
import { useRouter } from 'next/router'

import { colors, icons, zeroPad } from '../utils'
import { Modal } from './Modal'
import { CTA } from './CTA'
import { H3 } from './H3'
import { P } from './P'
import { LOCALSTORAGE } from '../utils/localStorage'
import { EventHookInterface } from '../types'
import { UserContext } from '../context'

const Div = styled.div`
  width: 600px;
  max-width: 100%;
  height: 700px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background-color: ${colors.white};

  .modalHeader {
    width: 405px;
    min-height: 87px;
    max-width: 100%;
    margin-bottom: 20px;
  }

  .modalContent {
    width: 405px;
    min-height: 100px;
    max-width: 100%;
    margin-bottom: 30px;
    text-align: center;
  }

  .modalContent.warning {
    height: 50px;

    .bold {
      font-weight: 600;
    }
  }

  .zoomImage {
    width: 70px;
    height: 70px;
  }

  .zoomContainer {
    width: 202.41px;
    height: 106.27px;
    margin-top: 19.95px;
    margin-bottom: 6.78px;
    display: flex;
    justify-content: center;
    align-items: center;
  }

  .button {
    width: 263px;

    &.authorizeButton {
    }

    &.cancelButton {
      margin-top: 15px;
    }
  }

  .popupError {
    margin-top: 10px;
  }
`

// Props.
interface Props {
  close: (clickYes) => void
  userId: number
  eventHook?: EventHookInterface
}

// Sign In To Join..
export const ZoomAuthoriseModal: React.FC<Props> = (props) => {
  const { t } = useTranslation('common')

  const router = useRouter()
  const { pathname } = router
  const { close, userId, eventHook } = props
  const { currentUser, createOAuthState } = useContext(UserContext)

  const [popupError, setPopupError] = useState(false)

  const request_user_auth_url = 'https://zoom.us/oauth/authorize'

  const getZoomRedirectURI = (): string => {
    const uri =
      process.env.NEXT_PUBLIC_ZOOM_REDIRECT_URI ||
      'http://local.ourklyk.com:8000/av/zoom/oauth-access'
    return encodeURIComponent(uri)
  }

  const getZoomClientId = (): string => {
    return process.env.NEXT_PUBLIC_ZOOM_CLIENT_ID || 'mIHLe6jFS7GI9cZlLhKRTw'
  }

  const getOAuthUrl = (oauthId: number): string => {
    return `${request_user_auth_url}?response_type=code&client_id=${getZoomClientId()}&redirect_uri=${getZoomRedirectURI()}&state=${oauthId}`
  }

  const background: React.CSSProperties = {
    background: `${colors.white}`,
    width: '600px',
    maxWidth: '100%',
    height: '700px',
    left: '50%',
    top: '50%',
    position: 'fixed',
    transform: 'translate(-50%, -50%)',
    overflow: 'hidden',
    borderRadius: '10px',
    boxShadow: '0px 10px 15px rgba(0, 0, 0, 0.1)',
  }

  const isValidDate = (date: Date) => {
    return date instanceof Date && !isNaN(date.getTime())
  }

  // Convert To ISO Date Time using user timezone if there is one
  const converToUTC = (date, time): string => {
    const calculatedTZ = MOMENT.tz.guess()
    const datetimestring = `${date.getFullYear()}-${
      date.getMonth() + 1
    }-${date.getDate()} ${zeroPad(time.getHours(), 2)}:${zeroPad(
      time.getMinutes(),
      2,
    )}:00`
    const mo = MOMENT.tz(datetimestring, 'YYYY-MM-DD HH-mm', calculatedTZ)
    return mo.utc().toISOString(true)
  }

  // ..
  return (
    <Modal
      style={background}
      close={() => close(false)}
      retainPointerEvents={true}
      zIndex={9999}>
      <Div className="modalContainer">
        {/* <div className="crossContainer" onClick={close}>
          <img src={icons.close} className="crossImage"/>
        </div> */}
        <div className="zoomContainer">
          <img src={icons.zoom} className="zoomImage"></img>
        </div>
        <H3 className="modalHeader">
          {t(
            `ZoomAuthoriseModal-1979115626`,
            `To create your first event, we’ll need to sync with your Zoom account.`,
          )}
        </H3>
        <P className="detail modalContent">
          {t(
            `ZoomAuthoriseModal-1061675228`,
            `Click the button below to authorise your Zoom account. This will allow us to know details, like whether you are using a basic or pro account, and give Klyk the ability to create a Zoom link automatically. If you want to use your own link, just click cancel and insert it where prompted.`,
          )}
        </P>
        {eventHook && (
          <P className="detail modalContent warning">
            <span className="bold">{t(`Warning:`, `Warning:`)}</span>
            {t(
              `ZoomAuthoriseModal-1479750212`,
              ` Authorizing now may discard the current event you’re setting up (happens only this one time)`,
            )}
          </P>
        )}
        <CTA
          className="purple button authorizeButton"
          onClick={async () => {
            setPopupError(false)
            if (eventHook) {
              eventHook.saveDraft()
            }
            const result = await createOAuthState(pathname)
            if (result) {
              const win = window.open(getOAuthUrl(result.id), '_blank')
              if (win) {
                win.focus()
                close(true)
              } else {
                setPopupError(true)
              }
            }
          }}>
          {t(`Authorise Zoom`, `Authorise Zoom`)}
        </CTA>
        {popupError && (
          <P className="detail warning popupError">
            {t(
              `ZoomAuthoriseModal-491295069`,
              `Authorise pop-up window is blocked by the browser`,
            )}
          </P>
        )}
        <CTA className="button cancelButton" onClick={() => close(false)}>
          {t(`Cancel`, `Cancel`)}
        </CTA>
      </Div>
    </Modal>
  )
}
